import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { type ReactNode, forwardRef } from 'react';
import { cn } from '../utils/cn';

interface SelectOption<T extends string = string> {
  value: T;
  label: ReactNode;
}

interface SelectProps<T extends string = string> {
  value: T;
  onValueChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

export function Select<T extends string = string>({
  value,
  onValueChange,
  options,
  placeholder,
  className,
  ariaLabel,
  disabled,
}: SelectProps<T>) {
  return (
    <RadixSelect.Root
      value={value}
      onValueChange={(v) => onValueChange(v as T)}
      disabled={disabled}
    >
      <RadixSelect.Trigger
        aria-label={ariaLabel}
        className={cn(
          'inline-flex w-full items-center justify-between gap-3 ' +
            'bg-surface text-ink text-[15px] px-3.5 py-2.5 ' +
            'border border-rule-strong hover:border-ink ' +
            'focus:outline-none focus:ring-2 focus:ring-petrol/25 focus:border-petrol ' +
            'transition-colors duration-150 rounded-md disabled:opacity-50',
          className,
        )}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon>
          <ChevronDown className="size-4 opacity-70" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          className="z-50 min-w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)] overflow-hidden border border-rule-strong bg-surface rounded-md surface elev-pop"
          position="popper"
          sideOffset={4}
        >
          <RadixSelect.Viewport className="p-1 max-h-[var(--radix-select-content-available-height)] overflow-y-auto">
            {options.map((opt) => (
              <Item key={opt.value} value={opt.value}>
                {opt.label}
              </Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

const Item = forwardRef<HTMLDivElement, { value: string; children: ReactNode }>(
  ({ value, children }, ref) => (
    <RadixSelect.Item
      ref={ref}
      value={value}
      className="relative flex items-center gap-2 px-2.5 py-2 text-[15px] rounded-sm select-none cursor-default outline-none data-[highlighted]:bg-surface-tint data-[state=checked]:font-semibold"
    >
      <RadixSelect.ItemIndicator className="absolute start-[-4px] inline-flex items-center">
        <Check className="size-3" />
      </RadixSelect.ItemIndicator>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  ),
);
Item.displayName = 'SelectItem';
